/**
 * Size DHTMLX grid columns to header/content width so the grid can scroll
 * horizontally; users may then shrink columns via resize handles.
 *
 * Used when the quantity panel is shown (grid-only layout). Schedule / Look ahead
 * use applyScheduleGridColumnLayout / disableGanttGridContentScroll (fill vs scroll).
 */

import {
  applyFixedColumnWidths,
  chooseGridColumnLayoutMode,
  FLEX_GANTT_COLUMN_NAME,
  sessionColumnWidthMap,
  sumVisibleColumnWidths,
  type GridColumnLayoutMode,
} from './ganttColumnWidths';

type GanttColumn = {
  name?: string;
  label?: string;
  width?: number;
  min_width?: number;
  hide?: boolean;
  template?: (task: unknown) => unknown;
};

type GanttLike = {
  config?: {
    columns?: GanttColumn[];
    grid_width?: number;
    min_grid_column_width?: number;
    autofit?: boolean;
    grid_elastic_columns?: boolean;
  };
  getGridColumns?: () => GanttColumn[];
  eachTask?: (cb: (task: Record<string, unknown>) => void) => void;
  getTaskByIndex?: (index: number) => Record<string, unknown> | null;
  getVisibleTaskCount?: () => number;
  getTaskCount?: () => number;
  $grid_scale?: HTMLElement | null;
  $root?: HTMLElement | null;
  $ui?: {
    getView?: (
      id: string,
    ) => { $config?: { scrollable?: boolean; width?: number } } | undefined;
  };
  setSizes?: () => void;
  render?: () => void;
};

type SavedScheduleGridState = {
  gridWidth?: number;
  columnWidths: Array<{ name?: string; width?: number }>;
};

const COLUMN_WIDTH_CAP: Record<string, number> = {
  srNo: 120,
  text: 520,
  start_date: 140,
  end_date: 140,
  duration: 96,
  progress: 96,
  wbs: 88,
  predecessors: 200,
  successors: 200,
  locationType: 180,
  taskType: 140,
};

/** Snapshot of Schedule grid proportions taken before Targets content-fit. */
let savedScheduleGridState: SavedScheduleGridState | null = null;
/** Last restored Schedule grid width — used if show_chart layout settles after restore. */
let lastRestoredScheduleGridWidth: number | null = null;

let measureCanvas: HTMLCanvasElement | null = null;

function measureTextWidth(text: string, font: string): number {
  if (!measureCanvas) measureCanvas = document.createElement('canvas');
  const ctx = measureCanvas.getContext('2d');
  if (!ctx) return text.length * 8;
  ctx.font = font;
  return ctx.measureText(text).width;
}

function stripHtml(html: string): string {
  if (!html) return '';
  if (!html.includes('<')) return html.trim();
  const el = document.createElement('div');
  el.innerHTML = html;
  return (el.textContent || el.innerText || '').replace(/\s+/g, ' ').trim();
}

function resolveFont(gantt: GanttLike): string {
  const el = gantt.$grid_scale;
  if (el) {
    const cs = window.getComputedStyle(el);
    return `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`.trim();
  }
  return '400 12px system-ui, sans-serif';
}

function getVisibleColumns(gantt: GanttLike): GanttColumn[] {
  return (gantt.getGridColumns?.() ?? gantt.config?.columns ?? []).filter(
    (c) => c && !c.hide,
  );
}

function snapshotScheduleGridState(gantt: GanttLike): void {
  if (savedScheduleGridState) return;
  const columns = getVisibleColumns(gantt);
  const grid = gantt.$ui?.getView?.('grid');
  const layoutWidth = grid?.$config?.width;
  const configWidth = gantt.config?.grid_width;
  const gridWidth =
    typeof layoutWidth === 'number' && layoutWidth > 0
      ? layoutWidth
      : typeof configWidth === 'number' && configWidth > 0
        ? configWidth
        : undefined;

  savedScheduleGridState = {
    gridWidth,
    columnWidths: columns.map((c) => ({
      name: c.name,
      width: typeof c.width === 'number' ? c.width : undefined,
    })),
  };
}

function defaultScheduleGridWidth(gantt: GanttLike): number {
  const rootWidth = gantt.$root?.clientWidth ?? 0;
  if (rootWidth > 0) {
    // Match typical initial Schedule split (~40–45% grid).
    return Math.round(Math.min(Math.max(rootWidth * 0.42, 360), rootWidth * 0.55));
  }
  return 520;
}

/**
 * Expand each visible column to fit header + sampled cell text, enable grid
 * horizontal scrolling, and refresh layout.
 */
export function fitGanttColumnsToContent(
  gantt: GanttLike | null | undefined,
  opts?: { sampleLimit?: number },
): void {
  if (!gantt?.config) return;
  const columns = getVisibleColumns(gantt);
  if (columns.length === 0) return;

  snapshotScheduleGridState(gantt);

  const sampleLimit = opts?.sampleLimit ?? 100;
  const pad = 32;
  const font = resolveFont(gantt);
  const minDefault = gantt.config.min_grid_column_width ?? 70;

  const samples: Record<string, unknown>[] = [];
  const visibleCount = gantt.getVisibleTaskCount?.() ?? gantt.getTaskCount?.() ?? 0;
  if (typeof gantt.getTaskByIndex === 'function' && visibleCount > 0) {
    const n = Math.min(sampleLimit, visibleCount);
    for (let i = 0; i < n; i += 1) {
      const task = gantt.getTaskByIndex(i);
      if (task) samples.push(task);
    }
  } else {
    gantt.eachTask?.((task) => {
      if (samples.length >= sampleLimit) return;
      samples.push(task);
    });
  }

  for (const col of columns) {
    const minW = Number(col.min_width) > 0 ? Number(col.min_width) : minDefault;
    const cap = COLUMN_WIDTH_CAP[String(col.name ?? '')] ?? 360;
    let next = minW;

    const label = String(col.label ?? col.name ?? '');
    if (label) next = Math.max(next, measureTextWidth(label, font) + pad);

    for (const task of samples) {
      let text = '';
      if (typeof col.template === 'function') {
        try {
          text = stripHtml(String(col.template(task) ?? ''));
        } catch {
          text = '';
        }
      } else if (col.name != null && task[col.name] != null) {
        text = String(task[col.name]);
      }
      if (!text) continue;
      // Tree column: leave room for indent + open icon.
      const treePad = col.name === 'text' ? 28 + Number(task.$level || 0) * 16 : 0;
      next = Math.max(next, measureTextWidth(text, font) + pad + treePad);
    }

    col.width = Math.round(Math.min(Math.max(next, minW), cap));
  }

  const total = columns.reduce((sum, c) => sum + (Number(c.width) || 0), 0);
  gantt.config.grid_width = Math.max(total, minDefault);
  gantt.config.autofit = false;

  const grid = gantt.$ui?.getView?.('grid');
  if (grid?.$config) {
    grid.$config.scrollable = true;
    grid.$config.width = gantt.config.grid_width;
  }

  gantt.setSizes?.();
  gantt.render?.();
}

/**
 * Schedule / Look ahead layout: restore panel width, apply session column widths,
 * and enable grid H-scroll when columns overflow the panel (else fill + elastic text).
 */
export function applyScheduleGridColumnLayout(
  gantt: GanttLike | null | undefined,
  opts?: {
    /** Live session width overrides (merged with defaults). */
    liveWidths?: Partial<Record<string, number>> | null;
    /** Preferred grid panel width (px); wins over module/default fallbacks. */
    preferredGridWidth?: number;
    /** Skip gantt.render (caller will paint). */
    skipRender?: boolean;
  },
): GridColumnLayoutMode {
  if (!gantt?.config) return 'fill';

  const columns = getVisibleColumns(gantt);
  const saved = savedScheduleGridState;
  if (saved) savedScheduleGridState = null;

  if (saved) {
    for (const prev of saved.columnWidths) {
      if (prev.name == null || prev.width == null) continue;
      const col = columns.find((c) => c.name === prev.name);
      if (col) col.width = prev.width;
    }
  }

  const preferred =
    typeof opts?.preferredGridWidth === 'number' && opts.preferredGridWidth > 0
      ? opts.preferredGridWidth
      : null;

  const restoredWidth =
    preferred != null
      ? preferred
      : saved?.gridWidth != null && saved.gridWidth > 0
        ? saved.gridWidth
        : lastRestoredScheduleGridWidth != null && lastRestoredScheduleGridWidth > 0
          ? lastRestoredScheduleGridWidth
          : defaultScheduleGridWidth(gantt);

  lastRestoredScheduleGridWidth = restoredWidth;
  gantt.config.grid_width = restoredWidth;

  const widthMap = sessionColumnWidthMap(opts?.liveWidths);
  // Pre-apply in fill mode to get stable non-flex widths for the sum check.
  applyFixedColumnWidths(columns, widthMap, {
    flexColumn: FLEX_GANTT_COLUMN_NAME,
    mode: 'fill',
  });
  // For the sum, use map for text as well so overflow accounts for preferred Task name width.
  const sumWidths = sumVisibleColumnWidths(
    columns.map((c) =>
      c.name === FLEX_GANTT_COLUMN_NAME && typeof widthMap[FLEX_GANTT_COLUMN_NAME] === 'number'
        ? { ...c, width: widthMap[FLEX_GANTT_COLUMN_NAME] }
        : c,
    ),
    widthMap,
  );
  const mode = chooseGridColumnLayoutMode(sumWidths, restoredWidth);

  applyFixedColumnWidths(columns, widthMap, {
    flexColumn: FLEX_GANTT_COLUMN_NAME,
    mode,
  });

  if (mode === 'scroll') {
    gantt.config.autofit = false;
    gantt.config.grid_elastic_columns = false;
  } else {
    gantt.config.autofit = true;
    gantt.config.grid_elastic_columns = true;
  }

  const grid = gantt.$ui?.getView?.('grid');
  if (grid?.$config) {
    grid.$config.scrollable = mode === 'scroll';
    grid.$config.width = restoredWidth;
  }

  gantt.setSizes?.();
  if (!opts?.skipRender) gantt.render?.();
  return mode;
}

/** Restore Schedule grid split after Targets content-fit mode. */
export function disableGanttGridContentScroll(
  gantt: GanttLike | null | undefined,
  opts?: {
    autofit?: boolean;
    elasticColumns?: boolean;
    /** Preferred grid panel width (px); wins over module/default fallbacks. */
    preferredGridWidth?: number;
    /** Live session widths for fill-vs-scroll policy. */
    liveWidths?: Partial<Record<string, number>> | null;
  },
): void {
  applyScheduleGridColumnLayout(gantt, {
    liveWidths: opts?.liveWidths ?? null,
    preferredGridWidth: opts?.preferredGridWidth,
  });
}

/** Remember the live Schedule grid width for subsequent layout restores this page session. */
export function rememberScheduleGridWidth(width: number): void {
  if (typeof width === 'number' && width > 0) {
    lastRestoredScheduleGridWidth = width;
  }
}
